import {
  Action,
  AppViewConfig,
  CustomActionsPosition,
  CustomActionTarget,
  HomePageSearchBarMode,
  SpotterEmbedViewConfig,
} from '@thoughtspot/visual-embed-sdk';
import { IGNITE_ENVIRONMENTS } from '@/constants/environment';
//  Full ThoughSpot css variable list
//  thoughtspot/visual-embed-sdk/src/css-variables.ts

/*
 * Every color used by the overrides below, stored as `r, g, b` channels.
 * Use `rgba(name)` / `rgba(name, alpha)` instead of raw color strings, so a brand
 * color change only requires editing this palette.
 */
const palette = {
  white: '255, 255, 255',
  black: '0, 0, 0',

  brand: '71, 108, 81',
  accent: '67, 168, 164',
  accentDark: '49, 130, 127',
  accentLight: '232, 244, 243',
  accentLighter: '240, 249, 249',
  successBackground: '234, 251, 235',

  textPrimary: '68, 64, 60',
  textSecondary: '120, 113, 108',
  textStrong: '18, 15, 13',

  border: '214, 211, 209',
  borderSubtle: '236, 234, 233',

  surfaceHover: '243, 244, 247',
  surfaceMuted: '245, 245, 244',

  error: '226, 43, 61',

  tokenMeasure: '224, 248, 239',
  tokenAttribute: '222, 232, 250',
  tokenDate: '240, 235, 255',

  slate: '192, 198, 207',
  slateLight: '217, 221, 227',
  slateActive: '212, 218, 227',
  shadow: '25, 35, 49',
} as const;

const rgba = (color: keyof typeof palette, alpha = 1) => `rgba(${palette[color]}, ${alpha})`;

const customCssVariables = {
  /**
   * Background color of the Liveboard, visualization, Search, and Answer pages.
   */
  '--ts-var-root-background': rgba('white'),

  /**
   * Color of the text on application pages.
   */
  '--ts-var-root-color': rgba('textPrimary'),

  /**
   * Font type for the text on application pages.
   */
  '--ts-var-root-font-family': 'Inter, sans-serif',

  /**
   * Background color of the Spotter prompt/chat input panel.
   */
  // '--ts-var-spotter-prompt-background': '',

  /**
   * Text transformation specification for UI elements in the app.
   */
  // '--ts-var-root-text-transform': '',

  /**
   * Font color of the text on toggle buttons such as
   * **All**, **Answers**, and **Liveboards** on the Home page (Classic experience),
   * the text color of the chart and table tiles on Home page (New modular Homepage
   * experience), and title text on the AI-generated charts and tables.
   * The default color code is #2770EF.
   *
   */
  '--ts-var-application-color': rgba('brand'),

  /**
   * Background color of the top navigation panel.
   */
  // '--ts-var-nav-background': '',

  /**
   * Font color of the top navigation panel.
   */
  // '--ts-var-nav-color': '',

  /**
   * Background color of the *Search data* button.
   */
  // '--ts-var-search-data-button-background': '',

  /**
   * Color of the text on the *Search data* button.
   */
  // '--ts-var-search-data-button-font-color': '',

  /**
   * Font of the text on the *Search data* button.
   */
  // '--ts-var-search-data-button-font-family': '',

  /**
   * Font color of the text in the Search bar.
   */
  // '--ts-var-search-bar-text-font-color': '',

  /**
   * Font of the text in the Search bar.
   */
  // '--ts-var-search-bar-text-font-family': '',

  /**
   * Font style of the text in the Search bar.
   */
  // '--ts-var-search-bar-text-font-style': '',

  /**
   * Background color of the search bar.
   */
  // '--ts-var-search-bar-background': '',

  /**
   * Background color of the search suggestions panel.
   */
  // '--ts-var-search-auto-complete-background': '',

  /**
   * Background color of the navigation panel that allows you to undo, redo, and reset
   * search operations.
   */
  // '--ts-var-search-navigation-button-background': '',

  /**
   * Background color of the navigation help text that appears at the bottom of the
   * search suggestions panel.
   */
  // '--ts-var-search-bar-navigation-help-text-background': '',

  /**
   * Background color of the search suggestion block on hover.
   */
  // '--ts-var-search-bar-auto-complete-hover-background': '',

  /**
   * Font color of the text in the search suggestion panel.
   */
  // '--ts-var-search-auto-complete-font-color': '',

  /**
   * Font color of the sub-text that appears below the keyword in the search suggestion
   * panel.
   */
  // '--ts-var-search-auto-complete-subtext-font-color': '',

  /**
   * Background color of the data panel.
   */
  // '--ts-var-answer-data-panel-background-color': '',

  /**
   * Background color of the vertical panel on the right side of the Answer page, which
   * includes the options to edit charts and tables.
   */
  // '--ts-var-answer-edit-panel-background-color': '',

  /**
   * Background color of the chart switcher on search results and Answer pages.
   */
  // '--ts-var-answer-view-table-chart-switcher-background': '',

  /**
   * Background color of the currently selected chart type in the chart switcher.
   */
  // '--ts-var-answer-view-table-chart-switcher-active-background': '',

  /**
   * Border-radius of main buttons.
   * For example, the *Search data* button in the top navigation panel.
   */
  // '--ts-var-button-border-radius': '',

  /**
   * Border-radius of small buttons such as secondary buttons.
   * For example, share and favorite buttons.
   */
  // '--ts-var-button--icon-border-radius': '',

  /**
   * Font color of the text on primary buttons. For example, the primary buttons on
   * Liveboard*, Answer, *Data* workspace, *SpotIQ*, or *Home* page.
   */
  '--ts-var-button--primary-color': rgba('white'),

  /**
   *
   */
  '--ts-var-liveboard-edit-bar-background': rgba('white'),

  /**
   * Font family specification for the text on primary buttons.
   */
  '--ts-var-button--primary--font-family': 'Inter, system-ui',

  /**
   * Background color of the primary buttons. For example, the primary buttons such as
   * Pin* and *Save*.
   */
  '--ts-var-button--primary-background': rgba('brand'),

  /**
   * Background color of the primary buttons on hover.
   */
  '--ts-var-button--primary--hover-background': rgba('brand', 0.9),

  /**
   * Backgroud color of the primary buttons when active.
   */
  '--ts-var-button--primary--active-background': rgba('brand'),

  /**
   * Font color of the text on the secondary buttons.
   */
  '--ts-var-button--secondary-color': rgba('brand'),

  /**
   * Font family specification for the text on the secondary buttons.
   */
  // '--ts-var-button--secondary--font-family': '',

  /**
   * Background color of the secondary buttons.
   */
  '--ts-var-button--secondary-background': rgba('slateLight'),

  /**
   * Background color of the secondary button on hover.
   */
  '--ts-var-button--secondary--hover-background': rgba('slateLight'),

  /**
   * Backgroud color of the secondary buttons when active.
   */
  // '--ts-var-button--secondary--active-background': '',

  /**
   * Font color of the tertiary button. For example, the *Undo*, *Redo*, and *Reset*
   * buttons on the *Search* page.
   */
  '--ts-var-button--tertiary-color': rgba('brand'),

  /**
   * Background color of the tertiary button.
   */
  '--ts-var-button--tertiary-background': rgba('white'),

  /**
   * Background color of the tertiary button when a user hovers over these buttons.
   */
  '--ts-var-button--tertiary--hover-background': rgba('surfaceHover'),

  /**
   * Backgroud color of the tertiary buttons when active.
   */
  '--ts-var-button--tertiary--active-background': rgba('slateActive'),

  /**
   * Font color of the title text of a visualization or Answer.
   */
  // '--ts-var-viz-title-color': '',

  /**
   * Font family specification for the title text of a visualization/Answer.
   */
  // '--ts-var-viz-title-font-family': '',

  /**
   * Text transformation specification for visualization and Answer titles.
   */
  // '--ts-var-viz-title-text-transform': '',

  /**
   * Font color of the description text and subtitle of a visualization or Answer.
   */
  // '--ts-var-viz-description-color': '',

  /**
   * Font family specification of description text and subtitle of a visualization or
   * Answer.
   */
  // '--ts-var-viz-description-font-family': '',

  /**
   * Text transformation specification for  description text and subtitle of a
   * visualization or Answer.
   */
  // '--ts-var-viz-description-text-transform': '',

  /**
   * Border-radius for the visualization tiles and header panel on a Liveboard.
   */
  // '--ts-var-viz-border-radius': '',

  /**
   * Box shadow property for the visualization tiles and header panel on a Liveboard.
   */
  // '--ts-var-viz-box-shadow': '',

  /**
   * Background color of the visualization tiles and header panel on a Liveboard.
   */
  // '--ts-var-viz-background': '',

  /**
   * Background color of the legend on a visualization or Answer.
   */
  // '--ts-var-viz-legend-hover-background': '',

  /**
   * Background color of the selected chart type on the chart selection widget.
   */
  // '--ts-var-answer-chart-select-background': '',

  /**
   * Background color of the chart type element when a user hovers over a chart type on
   * the chart selection widget.
   */
  // '--ts-var-answer-chart-hover-background': '',

  /**
   * Border-radius of filter chips.
   */
  // '--ts-var-chip-border-radius': '',

  /**
   * Shadow effect for filter chips.
   */
  // '--ts-var-chip-box-shadow': '',

  /**
   * Background color of filter chips.
   */
  // '--ts-var-chip-background': '',

  /**
   * Font color of the filter label when a filter chip is selected
   */
  // '--ts-var-chip--active-color': '',

  /**
   * Background color of the filter chips when selected.
   */
  // '--ts-var-chip--active-background': '',

  /**
   * Font color of the text on filter chips when hovered over.
   */
  // '--ts-var-chip--hover-color': '',

  /**
   * Background color of filter chips on hover.
   */
  // '--ts-var-chip--hover-background': '',

  /**
   * Font color of the text on filter chips.
   */
  // '--ts-var-chip-color': '',

  /**
   * Font family specification for the text on filter chips.
   */
  // '--ts-var-chip-title-font-family': '',

  /**
   * Font color of axis title on charts.
   */
  // '--ts-var-axis-title-color': '',

  /**
   * Font family specification for the X and Y axis title text.
   */
  // '--ts-var-axis-title-font-family': '',

  /**
   * Font color of the X and Y axis labels.
   */
  // '--ts-var-axis-data-label-color': '',

  /**
   * Font family specification for X and Y axis labels.
   */
  // '--ts-var-axis-data-label-font-family': '',

  /**
   * Font color of the menu items.
   */
  // '--ts-var-menu-color': '',

  /**
   * Background color of menu panels.
   */
  // '--ts-var-menu-background': '',

  /**
   * Font family specification for the menu items.
   */
  // '--ts-var-menu-font-family': '',

  /**
   * Text capitalization specification for the menu items.
   */
  // '--ts-var-menu-text-transform': '',

  /**
   * Background color for menu items on hover.
   */
  // '--ts-var-menu--hover-background': '',

  /**
   * Text color for selected menu items.
   */
  '--ts-var-menu-selected-text-color': rgba('accentDark'),

  /**
   * Background color of the dialogs.
   */
  // '--ts-var-dialog-body-background': '',

  /**
   * Font color of the body text displayed on dialogs.
   */
  // '--ts-var-dialog-body-color': '',

  /**
   * Background color of the header text on dialogs.
   */
  // '--ts-var-dialog-header-background': '',

  /**
   * Font color of the header text on dialogs.
   */
  // '--ts-var-dialog-header-color': '',

  /**
   * Background color of the footer area on dialogs.
   */
  // '--ts-var-dialog-footer-background': '',

  /**
   * Background for selected state in list
   */
  '--ts-var-list-selected-background': rgba('accentLight'),

  /**
   * Background for hover state in list
   */
  '--ts-var-list-hover-background': rgba('accentLight'),

  /**
   * Background for hover state in segment control.
   */
  // '--ts-var-segment-control-hover-background': '',

  /**
   * Text color for slected item in modular home's watchlist.
   */
  // '--ts-var-home-watchlist-selected-text-color': '',

  /**
   * Text color for favorite carousel find your favorites card in modular home.
   */
  // '--ts-var-home-favorite-suggestion-card-text-color': '',

  /**
   * Icon color for favorite carousel find your favorites card in modular home.
   */
  // '--ts-var-home-favorite-suggestion-card-icon-color': '',

  /**
   * Background for favorite carousel find your favorites card in modular home.
   */
  // '--ts-var-home-favorite-suggestion-card-background': '',
  /**
   * Border color of checkbox in error state.
   */
  // '--ts-var-checkbox-error-border': '',
  /**
   * Border color of checkbox.
   */
  // '--ts-var-checkbox-border-color': '',
  /**
   * Border color of checkbox in hover state.
   */
  '--ts-var-checkbox-hover-border': rgba('brand'),
  /**
   * Border and font color of checkbox in active state.
   */
  '--ts-var-checkbox-active-color': rgba('brand'),
  /**
   * Border color and font color of checkbox in checked state.
   */
  '--ts-var-checkbox-checked-color': rgba('brand'),
  /**
   * Border and font color of checkbox in disabled state.
   */
  // '--ts-var-checkbox-checked-disabled': '',
  /**
   * Background color of checkbox.
   */
  // '--ts-var-checkbox-background-color': '',

  /**
   * SpotterViz (renamed to SparkViz via stringOverrides) — Liveboard AI agent panel.
   */
  /**
   * Main panel background of the SpotterViz.
   */
  '--ts-var-spotterviz-panel-background': rgba('white'),
  /**
   * Background color of the chat input field in SpotterViz.
   */
  '--ts-var-spotterviz-input-background': rgba('white'),
  /**
   * Placeholder text color of the chat input field in SpotterViz.
   */
  '--ts-var-spotterviz-input-placeholder-color': rgba('textSecondary'),
  /**
   * CTA color of the chat input field in SpotterViz.
   */
  '--ts-var-spotterviz-input-cta-color': rgba('brand'),
  /**
   * CTA hover color of the chat input field in SpotterViz.
   */
  '--ts-var-spotterviz-input-cta-hover-color': rgba('brand', 0.9),
  /**
   * Text color for the SpotterViz label in the empty state.
   */
  '--ts-var-spotterviz-emptystate-spotterviz-color': rgba('brand'),
  /**
   * Background color of the starter prompt cards in SpotterViz.
   */
  '--ts-var-spotterviz-prompt-card-background': rgba('white'),
  /**
   * Background hover color of the starter prompt cards in SpotterViz.
   */
  '--ts-var-spotterviz-prompt-card-hover-background': rgba('slate', 0.12),
  /**
   * Primary text color in SpotterViz (also tool response text and feedback buttons).
   */
  '--ts-var-spotterviz-text-primary': rgba('textPrimary'),
  /**
   * Secondary text color in SpotterViz.
   */
  '--ts-var-spotterviz-text-secondary': rgba('textSecondary'),
  /**
   * Background color of the user chat message bubble in SpotterViz.
   */
  '--ts-var-spotterviz-message-background': rgba('brand'),
  /**
   * Hover color for the user chat message expand button in SpotterViz.
   */
  '--ts-var-spotterviz-usermessage-icon-hover': rgba('slateActive'),
  /**
   * Background color of the user chat message expand button in SpotterViz.
   */
  '--ts-var-spotterviz-usermessage-icon-background': rgba('surfaceHover'),
  /**
   * Color of the thinking step header when in progress in SpotterViz.
   */
  '--ts-var-spotterviz-thinking-inprogress-header-color': rgba('textPrimary'),
  /**
   * Color of the thinking step header when completed in SpotterViz.
   */
  '--ts-var-spotterviz-thinking-completed-header-color': rgba('textSecondary'),
  /**
   * Color of the final completion indicator icon shown when all work is done.
   */
  '--ts-var-spotterviz-thinking-work-done-icon-color': rgba('brand'),
  /**
   * Hover background color for the working/work done CTA in SpotterViz.
   */
  '--ts-var-spotterviz-thinking-cta-hover-background': rgba('surfaceHover'),
  /**
   * Background color of tool call panels in SpotterViz.
   */
  '--ts-var-spotterviz-tool-call-background': rgba('surfaceHover'),
  /**
   * Color of tool titles and icons in SpotterViz.
   */
  '--ts-var-spotterviz-tool-title-color': rgba('brand'),
  /**
   * Border color of tool call panels in SpotterViz.
   */
  '--ts-var-spotterviz-tool-border-color': rgba('border'),
  /**
   * Background color of the JSON input panel inside a tool call in SpotterViz.
   */
  '--ts-var-spotterviz-tool-json-input-background': rgba('white'),
  /**
   * Text color of the JSON input panel inside a tool call in SpotterViz.
   */
  '--ts-var-spotterviz-tool-json-input-color': rgba('textPrimary'),
  /**
   * Background color of the upvote/downvote feedback buttons in SpotterViz.
   */
  '--ts-var-spotterviz-tool-feedback-button-background': rgba('brand', 0.1),
  /**
   * Hover background color of the upvote/downvote feedback buttons in SpotterViz.
   */
  '--ts-var-spotterviz-tool-feedback-button-hover': rgba('brand', 0.2),
  /**
   * Background color of the last checkpoint section in SpotterViz.
   */
  '--ts-var-spotterviz-last-checkpoint-background': rgba('brand', 0.1),
  /**
   * Border color of the last checkpoint section in SpotterViz.
   */
  '--ts-var-spotterviz-last-checkpoint-border': rgba('brand', 0.3),
  /**
   * Text color of the SpotterViz footer.
   */
  '--ts-var-spotterviz-footer-text-color': rgba('textSecondary'),
  /**
   * Shared border color used throughout SpotterViz: input box, user message,
   * header underline, left panel border, thinking step connector and dots.
   */
  '--ts-var-spotterviz-border-color': rgba('borderSubtle'),
  /**
   * Expanded user chat message bubble border color in SpotterViz.
   */
  '--ts-var-spotterviz-expanded-border-color': rgba('borderSubtle'),
  /**
   * Hover background of the unselected reference-mode toggle in the SpotterViz chat input.
   */
  '--ts-var-spotterviz-reference-icon-hover-background': rgba('surfaceHover'),
  /**
   * Icon color for the reference-mode selected state in SpotterViz.
   */
  '--ts-var-spotterviz-reference-icon-selected-color': rgba('brand'),
  /**
   * Background color for the reference-mode selected state in SpotterViz.
   */
  '--ts-var-spotterviz-reference-icon-selected-background': rgba('brand', 0.1),

  // Dashboard 'edit' view - Styling panel
  /**
   * Text color of the styling panel in the Liveboard.
   */
  '--ts-var-liveboard-styling-panel-text-color': rgba('textPrimary'),
  /**
   * Border color of the styling panel in the Liveboard.
   */
  '--ts-var-liveboard-styling-panel-border-color': rgba('border'),
  /**
   * Background color of the color palette in the Liveboard styling panel.
   */
  '--ts-var-liveboard-styling-color-palette-background': rgba('brand', 0.1),
  /**
   * Background color of the styling button in the Liveboard.
   */
  // '--ts-var-liveboard-styling-button-background': '',
  /**
   * Text color of the styling button in the Liveboard.
   */
  // '--ts-var-liveboard-styling-button-text-color': '',
  /**
   * Background color of the styling button in the Liveboard on hover.
   */
  '--ts-var-liveboard-styling-button-hover-background': 'transparent',
  /**
   * Text color of the styling button in the Liveboard on hover.
   */
  // '--ts-var-liveboard-styling-button-hover-text-color': '',
  /**
   * Background color of the styling button in the Liveboard when active.
   */
  '--ts-var-liveboard-styling-button-active-background': rgba('brand', 0.1),
  /**
   * Box shadow of the styling button in the Liveboard.
   */
  // '--ts-var-liveboard-styling-button-shadow': '',

  // Dashboard 'edit' view - Edit toolbar
  /**
   * Border color of the Liveboard edit header toolbar.
   */
  '--ts-var-liveboard-edit-toolbar-border': rgba('border'),
  /**
   * Text color of unselected items in the Liveboard edit header toolbar.
   */
  '--ts-var-liveboard-edit-toolbar-text': rgba('brand'),
  /**
   * Background color of the selected section in the Liveboard edit header toolbar.
   */
  '--ts-var-liveboard-edit-toolbar-selected-background': rgba('brand', 0.1),
  /**
   * Text color of the selected section in the Liveboard edit header toolbar.
   */
  '--ts-var-liveboard-edit-toolbar-selected-text-color': rgba('brand'),
  /**
   * Hover background color of unselected items in the Liveboard edit header toolbar.
   */
  '--ts-var-liveboard-edit-toolbar-hover-background': rgba('brand', 0.05),
  /**
   * Hover text color of unselected items in the Liveboard edit header toolbar.
   */
  '--ts-var-liveboard-edit-toolbar-hover-text-color': rgba('brand'),
};

const isLocalOrDev =
  import.meta.env.VITE_SENTRY_ENV === IGNITE_ENVIRONMENTS.LOCAL ||
  import.meta.env.VITE_SENTRY_ENV === IGNITE_ENVIRONMENTS.DEVELOPMENT;

const unstableCssVariables = {
  a: {
    color: `${rgba('brand')} !important`,
  },
  'a:hover': {
    'text-decoration': 'none !important',
    color: `${rgba('accent')} !important`,
  },
  '.trending-base-module__itemContainer:hover': {
    'background-color': `${rgba('accentLight')} !important`,
  },
  // Hide the ThoughtSpot footer logo
  '.footer-module__footerLogo': {
    display: 'none !important',
  },
  // sage icon
  '.icon-module__blue': {
    fill: `${rgba('brand')} !important`,
  },
  // sage search bar border
  '.eureka-search-bar-module__highlightSearchBar': {
    'border-color': `${rgba('brand')} !important`,
  },
  '.eureka-search-box-module__goButton': {
    'background-color': `${rgba('brand')} !important`,
  },
  '.eureka-search-box-module__goButton:hover': {
    'background-color': `${rgba('brand')} !important`,
  },
  // tag border
  '.tag-column-module__link': {
    'text-decoration': 'none !important',
    'border-color': `${rgba('brand')} !important`,
  },
  // liveboard table, author column
  '.avatar-module__nameLink:hover': {
    'text-decoration': 'none !important',
    color: `${rgba('brand')} !important`,
  },
  // liveboard table, hover icon (chevron)
  '.title-header-module__chevronDownIcon': {
    'border-color': `${rgba('brand')} !important`,
  },
  // home trending module, border
  '.home-trending-module__borderClassName:hover': {
    'border-color': `${rgba('brand')} !important`,
  },
  // sage sample questions, border
  '.eureka-seed-questions-module__seedQuestionWrapper:hover': {
    'border-color': `${rgba('brand')} !important`,
  },
  '.input-base-module__hasFocus': {
    'border-color': `${rgba('brand')} !important`,
  },
  // source chips
  '.virtualized-sources-module__selected': {
    'background-color': `${rgba('accentLighter')} !important`,
    'border-color': `${rgba('brand')} !important`,
  },
  // source modal, selected left filter right border
  '.tab-module__headerItemSelected': {
    'border-right-color': `${rgba('brand')} !important`,
  },
  // data selected tabe filter
  '.select-module__selectedValue': {
    color: `${rgba('brand')} !important`,
  },
  // data. cards in carousel
  '.carousel-card-module__cardHeader': {
    'background-image': `linear-gradient(90deg,${rgba('accent')},${rgba('brand')}) !important`,
  },
  '.homepage-carousel-card-module__pinboardMetricsCardHeader': {
    'background-image': `linear-gradient(90deg,${rgba('accent')},${rgba('brand')}) !important`,
  },
  // Add favorites modal select menu
  '.typography-module__accent': {
    color: `${rgba('brand')} !important`,
  },
  // toggle on background
  '.toggle-module__toggleSwitchMarker': {
    'background-color': `${rgba('brand')} !important`,
    'border-color': `${rgba('brand')} !important`,
  },
  // data, click carousel item
  '.bk-explorer-tab-item.select': {
    'border-bottom-color': `${rgba('brand')} !important`,
    color: `${rgba('brand')} !important`,
  },
  // data, , click carousel item, selected column
  '.slick-header-column-sorted': {
    color: `${rgba('brand')} !important`,
  },
  // ask sage
  '.chat-input-module__chatInputWrapper.chat-input-module__chatInputWrapperActive': {
    'border-color': `${rgba('brand')} !important`,
  },
  // as sage, send button
  '.chat-input-module__sendIconContainer': {
    background: `${rgba('brand')} !important`,
  },
  // spotIQ analyze modal
  '.wizard-modal-module__blueBar': {
    background: `${rgba('brand')} !important`,
  },
  '.spotiq-box-module__selectedQuestionCard': {
    'border-color': `${rgba('brand')} !important`,
  },
  // spotIQ, analysis modal, progress bar
  '.progress-bar-module__progressBarInner.progress-bar-module__greenBar': {
    'background-image': `linear-gradient(90deg,${rgba('accent')},${rgba('brand')}) !important`,
  },
  // liveboard kip annomally spinner
  '.loader-contextual-module__blue': {
    'border-color': `${rgba('brand')} !important`,
    'border-right-color': 'transparent !important',
  },
  '.library-header-module__libraryHeader': {
    display: 'flex !important',
    'flex-flow': 'row wrap !important',
    'justify-content': 'space-around !important',
    'align-items': 'anchor-center !important',
    gap: '.8571285715rem  !important',
    'padding-left': '1.7142857143rem !important',
  },
  // Dashboard and Charts Header Title and Seperator
  '.library-header-module__libraryHeader .library-header-module__libraryTitle': {
    margin: 'auto !important',
  },
  '.library-header-module__libraryHeader .library-header-module__librarySeperator': {
    margin: 'auto !important',
    width: '1px !important',
    height: '1.7142857143rem !important',
    'background-color': `${rgba('slate')} !important`,
  },
  // Dashboard Page Header Layout
  '.liveboard-container-module__layoutContainer': {
    background: `${rgba('white')} !important`,
    'margin-bottom': '1.7142857143rem !important',
    'min-height': '85vh !important',
    'flex-grow': '1 !important',
    '-webkit-flex-grow': '1 !important',
    '-ms-flex-grow': '1 !important',
    'margin-top': '.8571285715rem !important',
  },
  '.liveboards-module__container': {
    'margin-top': '.8571285715rem !important',
    'background-color': `${rgba('white')} !important`,
  },
  // Chart Page Header Layout
  '.answer-container-module__layoutContainer': {
    background: `${rgba('white')} !important`,
    'margin-bottom': '1.7142857143rem !important',
    'min-height': '85vh !important',
    'flex-grow': '1 !important',
    '-webkit-flex-grow': '1 !important',
    '-ms-flex-grow': '1 !important',
    'margin-top': '.8571285715rem !important',
  },
  '.answers-module__container': {
    'margin-top': '.8571285715rem !important',
    'background-color': `${rgba('white')} !important`,
  },
  '.ag-cell-focus, .ag-row-focus, .focus-visible': {
    'outline-color': `${rgba('brand')} !important`,
  },
  '.form-control-module__hasFocus .form-control-module__label': {
    color: `${rgba('brand')} !important`,
  },
  '.text-area-module__textarea:focus': {
    'border-color': `${rgba('brand')} !important`,
  },
  // Recently viewed by
  '.social-profile-module__usersGroup': {
    display: 'none !important',
  },
  /**
   * Data Page Customizations
   */
  // Hide specific Data workspace sections: Connections and Utilities
  'a[aria-label="Connections"]': {
    display: 'none !important',
  },
  'a[aria-label="Utilities"]': {
    display: 'none !important',
  },
  // Alternative selectors for Connections and Utilities based on href
  'a[href="#/data/embrace"]': {
    display: 'none !important',
  },
  'a[href="#/data/utilities"]': {
    display: 'none !important',
  },
  '#embrace': {
    display: 'none !important',
  },
  '#utilities': {
    display: 'none !important',
  },
  // Hide the Dependents tab on all ThoughtSpot models
  '.object-metadata-details-module__tabHeader > li.tab-module__headerItem[data-testid="tab-header-item-3"]': {
    display: 'none !important',
  },
  // Hide the Permission dropdown on the Share modal
  '.universal-share-dialog-share-list-module__permissionDropdown': {
    display: 'none !important',
  },
  '.universal-share-dialog-share-list-module__exclamationInfo': {
    display: 'none !important',
  },
  // this hides the left nav panel - mostly for the data/tables deeplink
  // '.layout-module__sidePanel': {
  //   display: 'none !important',
  // },
  // User preference deep link: hide all sections except notification preferences
  '.user-preference-module__nameContainer, .user-preference-module__preferenceContainer, .user-preference-module__securityContainer, .user-preference-module__experienceContainer, .user-preference-module__experienceContainer, .divider-module__includeSectionBorder':
    {
      display: 'none !important',
    },
  // Exposes system language preferences and hides the "Email for receiving notifications" row in user preferences deep link
  // Targets the LOCAL and DEVELOPMENT environments only
  ...(isLocalOrDev
    ? {
        '.user-preference-module__preferenceContainer': {
          display: 'block !important',
        },
        '.user-preference-module__preferenceContainer form > div:nth-child(2)': {
          display: 'none !important',
        },
      }
    : {}),
  // Expand button (e.g., "More details" button) text styling - only for tertiary buttons
  '.button-module__tertiary .typography-module__footnote.button-module__buttonText': {
    color: `${rgba('accentDark')} !important`,
    'margin-left': '.5714285714rem !important',
  },
  // Expand button (e.g., "More details" button) icon (chevron) styling - only for tertiary buttons
  // '.button-module__tertiary.button-module__buttonWithIcon .icon-module__iconStyle.icon-module__s': {
  //   fill: `${rgba('accentDark')} !important`,
  // },
  // Toast alert message colors
  '.toast-alert-module__container': {
    'background-color': `${rgba('successBackground')} !important`,
  },
  '.toast-alert-module__message': {
    color: `${rgba('brand')} !important`,
  },
  '.toast-alert-module__icon': {
    fill: `${rgba('brand')} !important`,
  },
  // Liveboard Highlights Toast
  '.pinboard-header-module__liveboardHighlightsToast.toast-alert-module__container.toast-alert-module__noHide': {
    border: `1px solid ${rgba('border')} !important`,
    'box-shadow': `0 10px 15px -3px ${rgba('black', 0.1)}, 0 4px 6px -4px ${rgba('black', 0.1)} !important`,
    background: `${rgba('white')} !important`,
  },
  '.pinboard-header-module__liveboardHighlightsToast .liveboard-highlights-module__toastLayout .liveboard-highlights-module__toastMessage':
    {
      color: `${rgba('textStrong')} !important`,
    },
  '.pinboard-header-module__liveboardHighlightsToast .liveboard-highlights-module__toastLayout .liveboard-highlights-module__toastMessage span':
    {
      color: `${rgba('textStrong')} !important`,
    },
  // 'Enable multiple sources' toggle
  '.choose-sources-v2-module__multipleSelectToggle': {
    display: 'none !important',
  },
  // Hide disabled tabs in the chart settings menu
  '.chart-settings-v2-menu-module__menuIcon.chart-settings-v2-menu-module__disabled.flex-layout-module__horizontal': {
    display: 'none !important',
  },
  // Hide "Connect to Slack" section in user profile
  '.user-preference-module__connectContainer': {
    display: 'none !important',
  },
  // Add KPI button icon on homepage
  '.homepage-watchlist-module__addKpiLinkIcon.icon-module__text-color': {
    fill: `${rgba('brand')} !important`,
  },
  // Dashboard 'edit' view
  '.liveboard-styling-edit-header-module__logoWrapper': {
    display: 'none !important',
  },
  '#styling-panel-close': {
    display: 'none !important',
  },
  '.liveboard-edit-toolbar-module__addDropdownMenu': {
    border: `1px solid ${rgba('borderSubtle')} !important`,
    'background-color': `${rgba('white')} !important`,
  },
  '.liveboard-edit-toolbar-module__addDropdownMenu .typography-module__p': {
    color: `${rgba('textPrimary')} !important`,
  },
  // Add menu icons fill with currentcolor, which ThoughtSpot sets to white
  '.liveboard-edit-toolbar-module__addDropdownMenu .menu-module__itemIcon': {
    color: `${rgba('textPrimary')} !important`,
  },
  '.liveboard-edit-toolbar-module__addDropdownMenu .menu-module__itemInfo': {
    color: `${rgba('textPrimary')} !important`,
  },
  // "Highlight tile" sparkle sets its own color (#fff) on the svg, so it ignores the itemIcon color
  '.liveboard-edit-toolbar-module__addDropdownMenu .liveboard-edit-toolbar-module__whiteSparkleIcon': {
    color: `${rgba('textPrimary')} !important`,
  },
  '.liveboard-edit-toolbar-module__toolbarContainer': {
    height: '34px !important',
  },
  '.liveboard-edit-toolbar-module__toolbarContainer .icon-module__gray': {
    fill: `${rgba('brand')} !important`,
  },
  '.liveboard-edit-toolbar-module__toolbarContainer .icon-module__mediumgray': {
    fill: `${rgba('brand')} !important`,
  },
  '.liveboard-edit-toolbar-module__segmentItem': {
    'border-left': `1px solid ${rgba('border')} !important`,
  },
  // Disabled segment keeps its resting color on hover (ThoughtSpot switches it to #dbdfe7)
  '.liveboard-edit-toolbar-module__segmentItem.liveboard-edit-toolbar-module__segmentItemDisabled:hover': {
    color: 'var(--ts-var-liveboard-edit-toolbar-text) !important',
  },
  '.liveboard-styling-panel-module__panelHeader.layout-module__horizontal': {
    'border-top': 'none !important',
  },
  '.liveboard-styling-panel-module__panelContainer': {
    'box-shadow': 'none !important',
  },
  '.liveboard-styling-panel-module__panelContainer .icon-module__text-color': {
    fill: `${rgba('white')} !important`,
  },
  '.liveboard-styling-panel-module__panelContainer.layout-module__vertical': {
    'border-top': `1px solid ${rgba('borderSubtle')} !important`,
    'border-left': `1px solid ${rgba('borderSubtle')} !important`,
  },
  '.liveboard-styling-panel-module__panelContainer .button-group-module__active:hover': {
    'background-color': `${rgba('brand', 0.1)} !important`,
  },
  '.liveboard-styling-panel-module__styleCheckboxControl': {
    border: 'none !important',
    'background-color': `${rgba('brand')} !important`,
  },
  '.liveboard-styling-panel-module__panelContainer .color-palette-module__colorPaletteSelector': {
    'border-color': `${rgba('border')} !important`,
    'background-color': `${rgba('white')} !important`,
  },
  '.liveboard-styling-panel-module__panelContainer .color-palette-module__colorPaletteSelectorOpen': {
    'background-color': `${rgba('white')} !important`,
  },
  '.liveboard-styling-panel-module__panelContainer .button-group-module__buttonGroupWrapper': {
    'border-color': `${rgba('border')} !important`,
  },
  // Button group (e.g. Corner style): icons default to near-white, the selected one to blue
  '.liveboard-styling-panel-module__panelContainer .button-group-module__buttonGroup svg.icon-module__text-color': {
    fill: `${rgba('textSecondary')} !important`,
    color: `${rgba('textSecondary')} !important`,
  },
  '.liveboard-styling-panel-module__panelContainer .button-group-module__buttonGroup.button-group-module__active': {
    'background-color': `${rgba('brand', 0.1)} !important`,
  },
  '.liveboard-styling-panel-module__panelContainer .button-group-module__buttonGroup.button-group-module__active svg.icon-module__text-color':
    {
      fill: `${rgba('brand')} !important`,
      color: `${rgba('brand')} !important`,
    },
  '.liveboard-styling-panel-module__fontSizeDropdownHeader.select-module__selectHead': {
    'background-color': `${rgba('white')} !important`,
    'border-color': `${rgba('border')} !important`,
  },
  '.liveboard-styling-panel-module__fontSizeDropdownHeader.select-module__selectHead.select-module__selectOpen': {
    'background-color': `${rgba('white')} !important`,
    'border-color': `${rgba('border')} !important`,
  },
  '.liveboard-styling-panel-module__fontSizeDropdownHeader.popover-module__positionable.popover-module__enableScroll.popover-module__in .menu-module__actionMenu.menu-module__withOutIcons':
    {
      border: `1px solid ${rgba('border')} !important`,
      'border-radius': '4px !important',
      'background-color': `${rgba('white')} !important`,
    },
  '.liveboard-styling-panel-module__fontSizeDropdownHeader.popover-module__positionable.popover-module__enableScroll.popover-module__in':
    {
      'border-radius': '4px !important',
      'margin-top': '4px !important',
      'box-shadow': 'none !important',
    },
  '.color-palette-module__colorPaletteMenu.popover-module__positionable.popover-module__in': {
    'box-shadow': 'none !important',
  },
  '.liveboard-styling-panel-module__fontSizeDropdownHeader .menu-module__itemText > p': {
    color: `${rgba('textPrimary')} !important`,
  },
  '.color-palette-module__colorPalette': {
    'border-color': `${rgba('border')} !important`,
  },
  '.menu-module__menuGroupWrapper .menu-module__itemInfo': {
    'background-color': `${rgba('white')} !important`,
  },
  '.menu-module__menuGroupWrapper .menu-module__itemInfo:hover': {
    'background-color': `${rgba('surfaceMuted')} !important`,
  },
  '.menu-module__menuGroupWrapper .icon-module__white': {
    fill: `${rgba('slate')} !important`,
  },
  // SparkViz
  '.spotter-viz-header-module__header': {
    'border-top': `1px solid ${rgba('borderSubtle')} !important`,
  },
  '.spotter-viz-prompt-card-module__promptCard:hover': {
    'border-color': `${rgba('borderSubtle')} !important`,
  },
  '.spotter-viz-chat-input-module__sendButton .icon-module__iconStyle.icon-module__m.icon-module__content-primary': {
    fill: `${rgba('white')} !important`,
  },
  '.spotter-viz-chat-input-module__sendButton:active:not(:disabled)': {
    opacity: '.4 !important',
    background: `${rgba('brand')} !important`,
  },
  '.spotter-viz-chat-input-module__chatInput': {
    color: `${rgba('textPrimary')} !important`,
  },
  // Rainbow gradient border on focus → brand border
  '.spotter-viz-chat-input-module__inputWrapper:not(:has(textarea:disabled)):focus-within': {
    background: `${rgba('white')} !important`,
    border: `1px solid ${rgba('borderSubtle')} !important`,
  },
  // Hide ThoughtSpot agent icons: by class, and by their `#rd-icon-spotter` / `#rd-icon-spotter-viz` sprites
  // spotterIconContainer also holds the gradient "thinking" pulse shown while a reply loads
  '.spotter-viz-message-item-module__agentIcon, .spotter-viz-landing-content-module__iconPlaceholder, svg:has(> use[*|href="#rd-icon-spotter"]), svg:has(> use[*|href="#rd-icon-spotter-viz"]), .chat-module__spotterIconContainer':
    {
      display: 'none !important',
    },
  // "Work done in N seconds" pill when expanded (hardcoded blue tint)
  '.spotter-viz-message-item-module__thinkingHeaderCompleteExpanded': {
    background: `${rgba('surfaceHover')} !important`,
  },
  '.spotter-viz-message-item-module__thinkingHeader:focus-visible, .spotter-viz-scroll-to-bottom-button-module__scrollButton:focus-visible':
    {
      outline: `2px solid ${rgba('brand')} !important`,
    },
  // Thinking steps box + connector line (hardcoded dark borders)
  '.spotter-viz-message-item-module__allThinking': {
    border: `1px solid ${rgba('borderSubtle')} !important`,
  },
  '.spotter-viz-message-item-module__currentThinking .spotter-viz-message-item-module__reasoningGroup:after': {
    'background-color': `${rgba('borderSubtle')} !important`,
  },
  // Scroll-to-bottom button (dark by default)
  '.spotter-viz-scroll-to-bottom-button-module__scrollButton': {
    background: `${rgba('white')} !important`,
    border: `1px solid ${rgba('borderSubtle')} !important`,
    'box-shadow': `0 0 4px ${rgba('shadow', 0.1)}, 0 2px 4px ${rgba('shadow', 0.04)} !important`,
  },
  '.spotter-viz-scroll-to-bottom-button-module__scrollButton:hover': {
    background: `${rgba('surfaceMuted')} !important`,
  },
  '.spotter-viz-scroll-to-bottom-button-module__scrollButton:active': {
    background: `${rgba('slateActive')} !important`,
  },
  '.spotter-viz-scroll-to-bottom-button-module__scrollButton svg': {
    fill: `${rgba('textPrimary')} !important`,
  },
  // Dark scrollbars
  '.spotter-viz-message-list-module__scrollContainer, .spotter-viz-landing-content-module__landingContent, .spotter-viz-chat-input-module__chatInput, .spotter-viz-error-display-module__errorDisplayContainer':
    {
      'scrollbar-color': `${rgba('border')} transparent !important`,
    },
  '.spotter-viz-message-list-module__scrollContainer::-webkit-scrollbar-thumb, .spotter-viz-landing-content-module__landingContent::-webkit-scrollbar-thumb, .spotter-viz-chat-input-module__chatInput::-webkit-scrollbar-thumb, .spotter-viz-error-display-module__errorDisplayContainer::-webkit-scrollbar-thumb, .spotter-viz-message-item-module__message.spotter-viz-message-item-module__userMessageExpanded::-webkit-scrollbar-thumb':
    {
      background: `${rgba('border')} !important`,
    },
  // Fade-out over collapsed long responses (fades to dark by default)
  '.spotter-viz-message-item-module__finalResponseSection.spotter-viz-message-item-module__collapsed:after': {
    background: `linear-gradient(to bottom, transparent, ${rgba('white')} 90%) !important`,
  },
  '.spotter-viz-message-item-module__message.spotter-viz-message-item-module__system': {
    background: `${rgba('surfaceHover')} !important`,
  },
  // Markdown in responses: links, inline code, code blocks, dividers
  '.spotter-viz-markdown-renderer-module__link, .spotter-viz-markdown-renderer-module__link:hover, .spotter-viz-footer-module__learnMoreLink.spotter-viz-footer-module__learnMoreLink.spotter-viz-footer-module__learnMoreLink':
    {
      color: `${rgba('brand')} !important`,
    },
  '.spotter-viz-markdown-renderer-module__code, .spotter-viz-markdown-renderer-module__pre': {
    background: `${rgba('surfaceHover')} !important`,
    'border-color': `${rgba('borderSubtle')} !important`,
    color: `${rgba('textPrimary')} !important`,
  },
  '.spotter-viz-markdown-renderer-module__hr': {
    'border-top-color': `${rgba('borderSubtle')} !important`,
  },
  // User chat bubble sits on the green message background — text defaults to text-primary
  '.spotter-viz-message-item-module__message.spotter-viz-message-item-module__user, .spotter-viz-message-item-module__message.spotter-viz-message-item-module__user *':
    {
      color: `${rgba('white')} !important`,
    },
  '.spotter-viz-message-item-module__message.spotter-viz-message-item-module__user': {
    'border-color': `${rgba('brand')} !important`,
  },
  // Upvote/downvote icons — icon fill defaults to text-primary
  '.spotter-viz-feedback-module__feedbackIcon': {
    fill: `${rgba('brand')} !important`,
  },
  // Feedback chip selected state (blue by default)
  '.spotter-viz-feedback-module__feedbackChipActive': {
    'border-color': `${rgba('brand')} !important`,
    background: `${rgba('brand', 0.1)} !important`,
    color: `${rgba('brand')} !important`,
  },
  // Checkpoint/version cards (dark by default)
  '.spotter-viz-version-card-module__previous, .spotter-viz-version-card-module__disabled': {
    'background-color': `${rgba('white')} !important`,
    'border-color': `${rgba('borderSubtle')} !important`,
  },
  '.spotter-viz-version-card-module__previous:hover': {
    'background-color': `${rgba('surfaceMuted')} !important`,
  },
  '.spotter-viz-version-card-module__disabled .spotter-viz-version-card-module__title': {
    color: `${rgba('textPrimary')} !important`,
  },
  // Loading states: panel spinner background + starter-prompt shimmer (dark by default)
  '.spotter-viz-host-module__loadingContainer': {
    background: `${rgba('white')} !important`,
  },
  '.spotter-viz-prompt-card-module__promptCardShimmer': {
    background: `${rgba('white')} !important`,
    'border-color': `${rgba('borderSubtle')} !important`,
  },
  '.spotter-viz-prompt-card-module__shimmerBar': {
    background: `linear-gradient(90deg, ${rgba('surfaceHover')} 25%, ${rgba('borderSubtle')}, ${rgba('surfaceHover')} 75%) !important`,
    'background-size': '400% 100% !important',
  },
  '.spotter-viz-prompt-card-module__promptCardDisabled:hover, .spotter-viz-prompt-card-module__promptCardDisabled:active':
    {
      'border-color': `${rgba('borderSubtle')} !important`,
      background: `${rgba('white')} !important`,
    },
  '.spotter-viz-prompt-card-module__promptCardDisabled .spotter-viz-prompt-card-module__promptText': {
    color: `${rgba('textSecondary')} !important`,
  },
  // Send button disabled state (white arrow on light gray by default)
  '.spotter-viz-chat-input-module__sendButton:disabled, .spotter-viz-zero-state-module__sendButton > button:disabled': {
    background: `${rgba('border')} !important`,
  },
  '.spotter-viz-chat-input-module__sendButton:disabled svg, .spotter-viz-chat-input-module__sendButton:disabled .icon-module__iconStyle.icon-module__m.icon-module__content-primary, .spotter-viz-zero-state-module__sendButton > button:disabled svg':
    {
      fill: `${rgba('textSecondary')} !important`,
      color: `${rgba('textSecondary')} !important`,
    },
  // Final response + error text (light dark-theme colors by default)
  '.spotter-viz-message-item-module__finalResponseSection, .spotter-viz-message-item-module__finalResponseSection h1, .spotter-viz-message-item-module__finalResponseSection h2, .spotter-viz-message-item-module__finalResponseSection h3, .spotter-viz-message-item-module__finalResponseSection h4, .spotter-viz-message-item-module__errorDescription, .spotter-viz-message-item-module__restoreErrorDescription, .spotter-viz-error-display-module__errorDescription':
    {
      color: `${rgba('textPrimary')} !important`,
    },
  '.spotter-viz-message-item-module__errorMessage, .spotter-viz-message-item-module__restoreErrorText, .spotter-viz-error-display-module__errorMessage, .tool-call-common-module__errorText':
    {
      color: `${rgba('error')} !important`,
    },
  '.spotter-viz-feedback-module__thankYouText': {
    color: `${rgba('textSecondary')} !important`,
  },
  // Tool call panel ("Generate answers"): text, separators and icons are hardcoded for dark theme
  '.tool-call-common-module__textContent, .tool-call-common-module__aiTilePrompt, .tool-call-common-module__noteDescription, .tool-call-common-module__actionInfo, .tool-call-common-module__keywords, .tool-call-common-module__query, .tool-call-common-module__completionMessage, .tool-call-common-module__datasetName, .tool-call-common-module__action':
    {
      color: `${rgba('textPrimary')} !important`,
    },
  '.tool-call-common-module__confidence, .tool-call-common-module__vizId': {
    color: `${rgba('textSecondary')} !important`,
  },
  '.tool-call-common-module__keyword': {
    color: `${rgba('brand')} !important`,
  },
  '.tool-call-common-module__separator': {
    'background-color': `${rgba('border')} !important`,
  },
  '.tool-call-common-module__jsonBox': {
    'border-color': `${rgba('border')} !important`,
  },
  '.tool-call-common-module__collapseButton svg, .tool-call-common-module__jsonBox svg': {
    fill: `${rgba('textPrimary')} !important`,
  },
  // Tool header icon and dataset icons: sprite fill ignores the tool title color (pale by default)
  '.tool-call-common-module__icon, .tool-call-common-module__icon use': {
    fill: `${rgba('brand')} !important`,
    color: `${rgba('brand')} !important`,
  },
  '.tool-call-common-module__datasetIcon, .tool-call-common-module__datasetIcon svg, .tool-call-common-module__datasetIcon use':
    {
      fill: `${rgba('textSecondary')} !important`,
      color: `${rgba('textSecondary')} !important`,
    },
  '.tool-call-common-module__errorIcon.tool-call-common-module__errorIcon': {
    fill: `${rgba('error')} !important`,
  },
  // Output query tokens: light tints instead of dark green/blue/purple chips
  '.tool-call-common-module__queryToken': {
    color: `${rgba('textPrimary')} !important`,
  },
  '.tool-call-common-module__queryTokenMeasure': {
    'background-color': `${rgba('tokenMeasure')} !important`,
  },
  '.tool-call-common-module__queryTokenAttribute': {
    'background-color': `${rgba('tokenAttribute')} !important`,
  },
  '.tool-call-common-module__queryTokenDate': {
    'background-color': `${rgba('tokenDate')} !important`,
  },
  '.tool-call-common-module__queryTokenFilter, .tool-call-common-module__queryTokenOther': {
    'background-color': `${rgba('white')} !important`,
    border: `1px solid ${rgba('border')} !important`,
  },
};

const stringOverrides = {
  Liveboard: 'Dashboard',
  liveboard: 'dashboard',
  Liveboards: 'Dashboards',
  liveboards: 'dashboards',
  Answer: 'Chart',
  answer: 'chart',
  Answers: 'Charts',
  answers: 'charts',
  Spotter: 'Spark',
  SpotterViz: 'SparkViz',
  Insights: 'Highlights',
  Insight: 'Highlight',
  insight: 'highlight',
  insights: 'highlights',
  'SpotIQ Analysis': 'AI Highlights',
  SpotIQ: 'AI Highlights',
  'Spot IQ Analyze': 'AI Highlights',
  'Monitor Subscriptions': 'Subscriptions',
  'No monitor subscriptions yet': 'No subscriptions yet',
};

const embedFrameParams = { height: '100vh' };

// Staging environments retain Query Visualizer and Query SQL for troubleshooting support.
// Local and development environments are treated the same as staging.
const isQueryDetailsHidden =
  import.meta.env.VITE_SENTRY_ENV !== IGNITE_ENVIRONMENTS.QA_STAGE6 &&
  import.meta.env.VITE_SENTRY_ENV !== IGNITE_ENVIRONMENTS.LOCAL &&
  import.meta.env.VITE_SENTRY_ENV !== IGNITE_ENVIRONMENTS.DEVELOPMENT;

// Custom configuration for non-creator users
const nonCreatorConfig: AppViewConfig = {
  homePageSearchBarMode: HomePageSearchBarMode.NONE,
  hiddenActions: [
    Action.AnswerDelete,
    Action.AskAi,
    Action.CreateLiveboard,
    Action.Download,
    Action.Edit,
    Action.ExportTML,
    Action.MakeACopy,
    Action.Pin,
    Action.Remove,
    Action.Save,
    Action.Share,
    Action.ShareViz,
    Action.ShowSageQuery,
    Action.SpotIQAnalyze,
    Action.SpotterFeedback,
    ...(isQueryDetailsHidden ? [Action.QueryDetailsButtons] : []),
  ],
};

const customShareHiddenActions: Action[] = [Action.Share, Action.ShareViz];

const customShareCustomActions = [
  {
    id: 'share-custom-action-liveboard-primary',
    name: 'Share',
    position: CustomActionsPosition.PRIMARY,
    target: CustomActionTarget.LIVEBOARD,
  },
  {
    id: 'share-custom-action-liveboard-menu',
    name: 'Share',
    position: CustomActionsPosition.MENU,
    target: CustomActionTarget.LIVEBOARD,
  },
  {
    id: 'share-custom-action-viz-primary',
    name: 'Share',
    position: CustomActionsPosition.PRIMARY,
    target: CustomActionTarget.VIZ,
  },
  {
    id: 'share-custom-action-viz-menu',
    name: 'Share',
    position: CustomActionsPosition.MENU,
    target: CustomActionTarget.VIZ,
  },
  {
    id: 'share-custom-action-answer-primary',
    name: 'Share',
    position: CustomActionsPosition.PRIMARY,
    target: CustomActionTarget.ANSWER,
  },
  {
    id: 'share-custom-action-answer-menu',
    name: 'Share',
    position: CustomActionsPosition.MENU,
    target: CustomActionTarget.ANSWER,
  },
];

const creatorConfig: AppViewConfig = {
  hiddenActions: [
    Action.ExportTML,
    Action.EditTML,
    Action.ImportTML,
    Action.SyncToOtherApps,
    Action.ManagePipelines,
    ...(isQueryDetailsHidden ? [Action.QueryDetailsButtons] : []),
  ],
};

const searchConfig: AppViewConfig = {
  enableCustomColumnGroups: true,
  dataPanelV2: true,
  additionalFlags: { enableCustomColumnGroups: true },
};

// `worksheetId` is set directly on the embed (auto_mode — see spark.tsx) since it identifies
// the data source, not a display customization.
//
// Everything below is the full set of SpotterEmbed config options (own + inherited from
// BaseViewConfig/ApiInterceptFlags), commented out by default so SDK defaults apply.
// Uncomment individual options to try them out one at a time.
const spotterConfig: Partial<Omit<SpotterEmbedViewConfig, 'worksheetId'>> = {
  // ----- Spotter-specific options (SpotterEmbedViewConfig) -----

  // Pre-populates the conversation with a starting search query instead of the blank chat state.
  // searchOptions: { searchQuery: 'Show me revenue by region' },

  // Disables data source (worksheet) selection while still showing which source is active.
  // disableSourceSelection: true,

  // Hides data source selection entirely (stronger than disableSourceSelection).
  // hideSourceSelection: true,

  // Shows the Spotter limitations disclaimer text underneath the chat input.
  showSpotterLimitations: true,

  // Hides the sample questions shown on the initial (empty) screen of the conversation.
  // hideSampleQuestions: true,

  // Applies runtime filters to the Spotter conversation, same shape as Liveboard/Answer filters.
  // Requires: import { RuntimeFilterOp } from '@thoughtspot/visual-embed-sdk';
  // runtimeFilters: [{ columnName: 'color', operator: RuntimeFilterOp.EQ, values: ['red'] }],

  // When true (the default since SDK 1.45.0), runtime filters are sent via the app init
  // payload instead of the iframe URL. Set to false for the pre-1.45.0 URL-append behavior.
  // excludeRuntimeFiltersfromURL: false,

  // Applies runtime parameters (worksheet-defined variables) to the conversation.
  // runtimeParameters: [{ name: 'Integer Param', value: 10 }],

  // Same URL-vs-payload toggle as excludeRuntimeFiltersfromURL, but for runtimeParameters.
  // excludeRuntimeParametersfromURL: false,

  // Enables the updated Spotter chat prompt UI/copy.
  // updatedSpotterChatPrompt: true,

  // Broader UX modernization of the Spotter experience, beyond just the chat prompt copy above.
  updatedSpotterExperience: true,

  // Shows the "radiance" visual affordance (glow/animation) around the Spotter surface.
  // showSpotterRadiance: true,

  // Sets the starting query mode for each new session: Fast Search vs Research Mode.
  // Requires: import { SpotterQueryMode } from '@thoughtspot/visual-embed-sdk';
  // defaultQueryMode: SpotterQueryMode.RESEARCH,

  // Pins the embed to a single Spotter Analyst (implies no default Spotter/"Show all").
  // Requires SDK 1.53.0+ (currently on 1.52.1).
  // spotterAnalystConfig: { analystId: '<analyst-guid>' },

  // Enables and relabels Spotter's conversation-sharing UI, so a conversation can become a
  // shareable artifact instead of private chat history.
  // spotterShareConversationConfig: {
  //   enableShareConversation: true,
  //   spotterShareLabel: 'Share insight',
  //   spotterShareModalTitle: 'Share this conversation',
  //   spotterShareConfirmLabel: 'Share',
  //   spotterShareCancelLabel: 'Cancel',
  // },

  // Opens a specific shared conversation directly in a read-only reader view.
  // sharedConversationId: 'shared-conversation-guid',

  // Data source GUIDs to make available in the conversation, in place of a single
  // worksheetId. Preferred over worksheetId when both are set, but feature-flagged.
  // dataSources: ['<data-source-guid>'],

  // Changes the embed's history handling to avoid back-button navigation loops.
  // overrideHistoryState: true,

  // Configures the past-conversations sidebar: visibility, title, default expanded state,
  // rename/delete/new-chat/best-practices labels, conversation pinning, and analyst relabeling.
  spotterSidebarConfig: {
    enablePastConversationsSidebar: true,
    spotterSidebarDefaultExpanded: true,
    spotterSidebarTitle: 'My Conversations',
    spotterChatRenameLabel: 'Rename',
    spotterChatDeleteLabel: 'Delete',
    spotterDeleteConversationModalTitle: 'Delete chat',
    spotterPastConversationAlertMessage: 'This is a past conversation.',
    spotterDocumentationUrl: 'https://help.alchemer.com/help/getting-started-with-spark',
    spotterBestPracticesLabel: 'Get Started with Spark',
    spotterConversationsBatchSize: 30,
    spotterNewChatButtonTitle: 'New Chat',
    // Conversation pinning (enable + rename pin/unpin labels). Requires SDK 1.53.0+ (currently on 1.52.1).
    // spotterChatPinConfig: { enabled: true, pinLabel: 'Pin', unpinLabel: 'Unpin' },
    // Relabels "Analyst"/"Analysts" wording in the sidebar (e.g. domain-specific agents).
    // spotterAnalystLabel: 'Analyst',
    // spotterAnalystsLabel: 'Analysts',
  },

  // Customizes ThoughtSpot branding on Spotter's tool response cards.
  // spotterChatConfig: {
  //   hideToolResponseCardBranding: false,
  //   toolResponseCardBrandingLabel: 'Alchemer',
  //   // Shows the starter-prompt pills on the initial (empty) screen of the conversation.
  //   enableStarterPrompts: true,
  //   // Allows uploading files (e.g. PDF, PNG, XLSX) in the Spotter chat input.
  //   spotterFileUploadEnabled: true,
  //   spotterFileUploadFileTypes: { types: ['pdf', 'png', 'xlsx'] },
  // },

  // ----- Inherited from BaseViewConfig (shared across embed types, SpotterEmbed included) -----

  // Greys out (but still shows) the listed actions instead of hiding them.
  // disabledActions: [Action.Download],

  // Tooltip text shown when hovering a disabled action.
  // disabledActionReason: 'Contact your admin to enable this action.',

  // Hides the listed actions entirely from menus/toolbars.
  // Hides the sidebar's "Analysts" section (requires ThoughtSpot Cloud 26.10.0.cl+).
  // hiddenActions: [Action.SpotterAnalystList], // hides "show all analysts"
  // hiddenActions: [Action.SpotterDefaultAnalyst], // hides "my conversations"
  // hiddenActions: [Action.SpotterAnalystList, Action.SpotterDefaultAnalyst], // hides "my conversations", leaves "show all analysts" shown
  hiddenActions: [Action.SpotterAnalystSidebar], // hides the whole Analysts panel

  // Allow-list of actions to show; everything else is hidden. Use instead of hiddenActions.
  // visibleActions: [Action.Download],

  // On Spotter, hiddenActions/visibleActions above can also target individual starter-prompt
  // pills: Action.QuickSearchPill, Action.DeepAnalysisPill, Action.DataLiteracyPill.
  // Requires SDK 1.53.0+ (currently on 1.52.1).

  // Locale for the embedded Spotter UI.
  // locale: 'en',

  // Escape hatch for internal/undocumented URL flags (SDK explicitly does not document keys).
  // additionalFlags: { someInternalFlag: true },

  // Per-embed override of ThoughtSpot string/CSS customizations. The app-wide version is
  // already applied globally in lib/thoughtspot.ts (buildEmbedConfig) — set this only to
  // override it just for Spotter.
  // customizations: { style: { customCSS: { variables: {} } } },

  // Inserts the iframe as a sibling of the target container instead of a child inside it.
  // insertAsSibling: true,

  // Reuses a pre-rendered iframe from a prerender pool matching this config, if available.
  // preRenderId: 'spark-prerender',

  // When using preRenderId, stops the prerender component from auto-tracking/resizing to
  // its container's size.
  // doNotTrackPreRenderSize: true,

  // Opts into the lighter-weight V2 embed shell for a potential performance improvement.
  // enableV2Shell_experimental: true,

  // Exposes translation IDs on the embedded app — useful for localization debugging.
  // exposeTranslationIDs: true,

  // Disables in-app links and prevents them from opening in a new tab.
  // disableRedirectionLinksInNewTab: true,

  // Overrides the Org context for the embedding user (requires Per Org URL feature enabled
  // on the ThoughtSpot instance).
  // overrideOrgId: 142536,

  // Overrides the "Open Link in New Tab" context menu option. Superseded by
  // enableLinkOverridesV2 below for improved handling.
  // linkOverride: true,

  // Enables the newer link-override mechanism, routing in-app navigation through the
  // EmbedEvent.DialogOpen event instead of native navigation.
  // enableLinkOverridesV2: true,

  // Shows ThoughtSpot's own alert/toast messages inside the embed.
  // showAlerts: true,

  // Adds custom UI actions (buttons/menu items) to the Spotter surface.
  // customActions: [
  //   {
  //     id: 'my-custom-action',
  //     name: 'My Custom Action',
  //     position: CustomActionsPosition.MENU,
  //     target: CustomActionTarget.SPOTTER,
  //   },
  // ],

  // Proactively refreshes the auth token when it's near expiry instead of waiting for a 401.
  // refreshAuthTokenOnNearExpiry: true,

  // Skips postMessage payload validation so events are processed even if old, incomplete,
  // or from another trusted system.
  // shouldBypassPayloadValidation: true,

  // Opts into the v2 Host Events API.
  // useHostEventsV2: true,

  // ----- Inherited from ApiInterceptFlags (part of BaseViewConfig) -----

  // Allows intercepting the OnBeforeGetVizDataIntercept event. SDK docs note this is usable
  // for Search and App Embed from SDK 1.29.0 — may not apply to SpotterEmbed.
  // isOnBeforeGetVizDataInterceptEnabled: true,

  // Which outgoing API calls to intercept, so their responses can be short-circuited via the
  // ApiIntercept event.
  // Requires: import { InterceptedApiType } from '@thoughtspot/visual-embed-sdk';
  // interceptUrls: [InterceptedApiType.ALL],

  // Timeout (ms) before an intercepted API call errors out. Default is 30000.
  // interceptTimeout: 1000,
};

export {
  customCssVariables,
  embedFrameParams,
  unstableCssVariables,
  stringOverrides,
  nonCreatorConfig,
  creatorConfig,
  customShareHiddenActions,
  customShareCustomActions,
  searchConfig,
  spotterConfig,
};
